import type { Citation } from '@ctw/shared/api/ai/types';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { compact, uniq } from 'lodash-es';
import { useCallback } from 'react';
import { CitationLink } from './citation-link';

/* https://stackoverflow.com/a/7784176 */
function nextNode(node: Node) {
  if (node.hasChildNodes()) {
    return node.firstChild;
  }

  let resolvedNode: Node | null = node;
  while (resolvedNode && !resolvedNode.nextSibling) {
    resolvedNode = resolvedNode.parentNode;
  }
  if (!resolvedNode) {
    return null;
  }

  return resolvedNode.nextSibling;
}

/* https://stackoverflow.com/a/7784176 */
function getRangeSelectedNodes(range: Range) {
  let node: Node | null = range.startContainer;
  const endNode = range.endContainer;

  // Special case for a range that is contained within a single node
  if (node === endNode) {
    return [node.nodeName.includes('text') ? node.parentNode : node];
  }

  // Iterate nodes until we hit the end container
  const rangeNodes: Array<Node | null> = [];
  let resolvedNode: Node | null = node;
  while (resolvedNode && resolvedNode !== endNode) {
    resolvedNode = nextNode(resolvedNode);
    rangeNodes.push(resolvedNode);
  }

  // Add partially selected nodes at the start of the range
  node = range.startContainer;
  while (node && node !== range.commonAncestorContainer) {
    rangeNodes.unshift(node);
    node = node.parentNode;
  }

  return rangeNodes;
}

/* https://stackoverflow.com/a/7784176 */
function getSelectedNodes() {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      return getRangeSelectedNodes(selection.getRangeAt(0));
    }
  }
  return [];
}

export type CitationTextProps = {
  className?: string;
  content: string;
  citations?: Array<Citation>;
};

export const CitationText = ({ content, citations }: CitationTextProps) => {
  const { trackInteraction } = useTelemetry();

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      return;
    }

    const selectedNodes = getSelectedNodes();
    const citationIds = uniq(
      compact(
        Array.from(selectedNodes).map((elem) =>
          elem instanceof Element ? elem.getAttribute('data-citation-reference-id') : undefined,
        ),
      ),
    );
    const citationDatas = (citations || []).filter((citation) =>
      citationIds.includes(`${citation.id}`),
    );

    trackInteraction('patient_summary_select_narrative_text', {
      citationIds: citationIds,
      resourceIds: citationDatas.map((citation) => citation.source_id),
      resourceTypes: citationDatas.map((citation) => citation.source_resource_type),
    });
  }, [citations, trackInteraction]);

  // break up the content into parts based on the citation references, breaks, etc
  const parts = content.split(/(\[\d+\]|<br>)/g).filter((part) => part.length > 0);

  // Remove leading/trailing breaks which happen sometimes
  while (parts.length > 0 && parts[0] === '<br>') {
    parts.shift();
  }
  while (parts.length > 0 && parts.at(-1) === '<br>') {
    parts.pop();
  }

  // only happens if there's an error in the API call
  if (!citations) {
    return <span>{content}</span>;
  }

  let lastCitation: Citation | undefined;
  return (
    <div onMouseUp={handleSelection}>
      {parts
        .reverse()
        .map((part) => {
          const citation = citations.find((c) => `[${c.id}]` === part);
          if (citation) {
            lastCitation = citation;
            return (
              <span data-citation-id={citation.id} key={`citation_${part}${citation.id}`}>
                <CitationLink
                  resourceInfo={{
                    resourceID: citation.source_id,
                    resourceType: citation.source_resource_type,
                  }}
                  citation={citation}
                />
              </span>
            );
          }

          if (part === '<br>') {
            return (
              <br
                data-citation-reference-id={lastCitation?.id}
                key={`${lastCitation?.id}_br_${part}`}
              />
            );
          }
          return (
            <span
              data-citation-reference-id={lastCitation?.id}
              key={`${lastCitation?.id}_span_${part}`}
            >
              {part}
            </span>
          );
        })
        .reverse()}
    </div>
  );
};
